/**
 * @module modules/plot-summary-sk/h_resizable_box_sk
 * @description <h2><code>h-resizable-box-sk</code></h2>
 *
 * This is created to support UI interactions for summary bar. This component
 * itself may be used for drawing and dragging a range horizontally.
 * The element will need to cover the entire parent node to work.
 *
 * @evt selection-changed - Emit when the selectionRange is changed.
 *  It is not bubbled, and its details contains the selectionRange.
 *
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "HResizableBoxSk", {
    enumerable: true,
    get: function() {
        return HResizableBoxSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _ref = require("lit/directives/ref.js");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var _HResizableBoxSk;
// clamps the value in between min and max. This helps to compute
// rect box not exceeding its boundary.
// if the given max is larger than min. val is clamped to max first.
const clamp = (val, min, max)=>{
    if (val > max) {
        return max;
    } else if (val < min) {
        return min;
    } else {
        return val;
    }
};
let HResizableBoxSk = (_HResizableBoxSk = class HResizableBoxSk extends _lit.LitElement {
    // We no longer need the setter to manually update styles.
    // The render method will handle it.
    onMouseDown(e) {
        // If the mouseDown is starting from our component, then we takes it over.
        // This disable system events like selecting texts.
        e.preventDefault();
        const box = this.selection.value;
        const rect = box.getBoundingClientRect();
        this.lastX = e.x;
        this.startX = e.x;
        if (e.target !== box) {
            this.action = 'draw';
            box.style.display = 'block'; // the selection box can be hidden if no selection.
        } else if (e.x - rect.left < this.handleWidth) {
            this.action = 'left';
        } else if (e.x > rect.right - this.handleWidth) {
            this.action = 'right';
        } else {
            this.action = 'drag';
        }
    }
    onMouseMove(e) {
        if (!this.action) {
            return;
        }
        e.preventDefault();
        const box = this.selection.value;
        const parentRt = this.getBoundingClientRect();
        const rect = box.getBoundingClientRect();
        const delta = this.lastX - e.x;
        const localLeft = rect.left - parentRt.left;
        const localRight = rect.right - parentRt.left;
        // We start to draw a new selection.
        if (this.action === 'draw') {
            // we drag the mouse to the left, and then we draw a box with a minWidth.
            if (e.x < this.startX) {
                const left = clamp(e.x - parentRt.left, 0, this.startX - parentRt.left - this.minWidth);
                box.style.left = left + 'px';
                box.style.width = clamp(this.startX - e.x, this.minWidth, parentRt.width - left) + 'px';
            } else {
                // We drag the mouse to the right.
                const left = clamp(this.startX - parentRt.left, 0, parentRt.width - this.minWidth);
                box.style.left = left + 'px';
                box.style.width = clamp(e.x - this.startX, this.minWidth, parentRt.width - left) + 'px';
            }
        }
        if (this.action === 'drag') {
            // We drag and move the selection box.
            box.style.left = clamp(localLeft - delta, 0, parentRt.width - rect.width) + 'px';
            box.style.width = rect.width + 'px';
        } else if (this.action === 'left') {
            // We drag the left edge of the box to resize it.
            const left = clamp(localLeft - delta, 0, localRight - this.minWidth);
            box.style.left = left + 'px';
            box.style.width = localRight - left + 'px';
        } else if (this.action === 'right') {
            // We drag the right edge of the box to resize it.
            const newLeft = rect.left - parentRt.left;
            box.style.left = rect.left - parentRt.left + 'px';
            box.style.width = clamp(rect.width - delta, this.minWidth, parentRt.width - newLeft) + 'px';
        }
        this.lastX = e.x;
    }
    onMouseUp() {
        if (this.action === null) {
            return;
        }
        this.action = null;
        this.dispatchEvent(new CustomEvent('selection-changed', {
            detail: this.computeSelectionFromDOM()
        }));
    }
    computeSelectionFromDOM() {
        const box = this.selection.value;
        if (!box || box.style.display === 'none') {
            return null;
        }
        const parentRt = this.getBoundingClientRect();
        const rect = box.getBoundingClientRect();
        return {
            begin: rect.left - parentRt.left,
            end: rect.right - parentRt.left
        };
    }
    render() {
        // If no selectionRange, hide the box.
        const display = this.selectionRange ? 'block' : 'none';
        // Calculate styles if visible
        let left = '0px';
        let width = '0px';
        if (this.selectionRange) {
            // access the host element's width safely
            const parentWidth = this.offsetWidth || 0;
            const range = this.selectionRange;
            const w = clamp(range.end - range.begin, this.minWidth, parentWidth - range.begin);
            const l = clamp(range.begin, 0, parentWidth);
            left = l + 'px';
            width = w + 'px';
        }
        return (0, _lit.html)`<div class="container" @mousedown=${(e)=>this.onMouseDown(e)}>
      <div
        class="surface"
        style="display: ${display}; left: ${left}; width: ${width};"
        ${(0, _ref.ref)(this.selection)}>
        <md-elevation></md-elevation>
      </div>
    </div>`;
    }
    connectedCallback() {
        super.connectedCallback();
        // We have mousedown event on the element so we start tracking that's
        // originated from ourselves. The default bounding box check helps us
        // initiate the tracking.
        // We listen to mousemove and moseup on Window so even the mouse is moving
        // outside the element, we can still get the callback.
        window.addEventListener('mousemove', (e)=>{
            this.onMouseMove(e);
        });
        window.addEventListener('mouseup', ()=>{
            this.onMouseUp();
        });
    }
    constructor(...args){
        super(...args);
        this.selection = (0, _ref.createRef)();
        // The handle bar width for resizing.
        this.handleWidth = 4;
        // The minimum width that allows dragging and resizing.
        this.minWidth = 24;
        // This tracks the current user intention:
        // * drag: the user moves the selection box;
        // * left: the user drags the left edge to resize;
        // * right: the user drags the right edge to resize;
        // * draw: the user starts a new selection.
        this.action = null;
        this.lastX = 0;
        this.startX = 0;
        this.selectionRange = null;
    }
}, (()=>{
    _HResizableBoxSk.styles = (0, _lit.css)`
    :host {
      display: block;
    }
    .container {
      position: absolute;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
    }
    .surface {
      display: none;
      position: absolute;
      border-radius: 6px;
      top: 0px;
      bottom: 0px;
      left: 0px;
      right: 0px;
      cursor: move;
      --md-elevation-level: 1;
      opacity: 0.5;
      background-color: var(--md-sys-color-primary-container, #ced0ce);
    }
    .surface::after {
      content: '';
      position: absolute;
      left: 0px;
      width: 4px;
      height: 100%;
      cursor: ew-resize;
    }
    .surface::before {
      content: '';
      position: absolute;
      right: 0px;
      width: 4px;
      height: 100%;
      cursor: ew-resize;
    }
  `;
})(), _HResizableBoxSk);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Object)
], HResizableBoxSk.prototype, "selectionRange", void 0);
HResizableBoxSk = _ts_decorate([
    (0, _decorators.customElement)('h-resizable-box-sk')
], HResizableBoxSk);
